import React, { useEffect, useMemo } from "react";
import { useGetInvestorAvailablePaymentRailsQuery } from "../../store/investor/investorInvestmentsApi";
import { InvestorSkeletonPulse } from "./InvestorDashboardSkeleton";

const RAIL_COPY = {
  paystack: {
    title: "Pay with card (Paystack)",
    subtitle: "Secure card checkout",
  },
  alatpay: {
    title: "Pay with ALATPay",
    subtitle: "Card, transfer, or USSD via ALATPay",
  },
};

/**
 * Online-only payment rail chooser for investments / marketplace / similar flows.
 * Uses org-configured rails from GET /investors/payments/available-rails.
 */
export default function OnlinePaymentRailsChooser({
  paymentProvider,
  onPaymentProviderChange,
  compact = false,
}) {
  const { data, isLoading } = useGetInvestorAvailablePaymentRailsQuery();
  const rails = useMemo(() => {
    const list = Array.isArray(data?.rails) ? data.rails.filter((r) => r && r.id) : [];
    return list.filter((r) => r.id === "paystack" || r.id === "alatpay");
  }, [data]);

  useEffect(() => {
    if (!rails.length) return;
    const ids = rails.map((r) => r.id);
    if (!ids.includes(paymentProvider)) {
      onPaymentProviderChange(ids[0]);
    }
  }, [rails, paymentProvider, onPaymentProviderChange]);

  if (isLoading && !rails.length) {
    return (
      <div className="mb-3" aria-busy="true">
        <InvestorSkeletonPulse height="14px" width="160px" className="mb-2" />
        <InvestorSkeletonPulse height="56px" width="100%" radius="12px" className="mb-2" />
        <InvestorSkeletonPulse height="56px" width="100%" radius="12px" />
      </div>
    );
  }

  if (!rails.length) {
    return (
      <p className="small text-danger mb-3">
        No online payment methods are available for this cooperative. Contact
        support or try again later.
      </p>
    );
  }

  if (rails.length === 1) {
    const only = rails[0];
    const copy = RAIL_COPY[only.id] || { title: only.label, subtitle: "" };
    return (
      <div className="mb-3">
        <p className={`fw-semibold text-black1 mb-1 ${compact ? "small" : ""}`}>
          Payment method
        </p>
        <p className="small text-muted mb-0">
          {copy.title}
          {copy.subtitle ? ` — ${copy.subtitle}` : ""}
        </p>
      </div>
    );
  }

  return (
    <div className="mb-3">
      <p className={`fw-semibold text-black1 mb-2 ${compact ? "small" : ""}`}>
        How do you want to pay?
      </p>
      <div className={`d-flex flex-column flex-md-row gap-${compact ? "2" : "3"}`}>
        {rails.map((rail) => {
          const copy = RAIL_COPY[rail.id] || { title: rail.label, subtitle: "" };
          const selected = paymentProvider === rail.id;
          return (
            <button
              key={rail.id}
              type="button"
              className={`flex-1 rounded-[10px] border-2 ${
                compact ? "py-2 px-3" : "py-3 px-4"
              } text-start bg-white`}
              style={{
                borderColor: selected ? "var(--color-primary)" : "#e0e0e0",
                boxShadow: selected
                  ? "0 0 0 1px rgba(139, 49, 58, 0.15)"
                  : "none",
              }}
              onClick={() => onPaymentProviderChange(rail.id)}
            >
              <div className="fw-semibold" style={{ color: "#383838" }}>
                {copy.title}
              </div>
              {copy.subtitle ? (
                <div className="small text-muted mt-1">{copy.subtitle}</div>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function hasOnlinePaymentRails(railsPayload) {
  const list = Array.isArray(railsPayload?.rails) ? railsPayload.rails : [];
  return list.some((r) => r?.id === "paystack" || r?.id === "alatpay");
}
